import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import apiClient from "../../services/apiClient";
import toast from "../../utils/toast";
import {
  forgotPassword,
  verifyOtp,
  resetPassword,
} from "../../services/auth/authServices";

const ROLE_ID = { saas: 1, owner: 2, tenant: 3 };
const ROLE_HOME = {
  SaasAdmin: "/admin/dashboard",
  PropertyOwner: "/owner/dashboard",
  Tenant: "/tenant/dashboard",
};

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [activeRole, setActiveRole] = useState("saas");
  const [form, setForm] = useState({ emailOrMobile: "", password: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // ── Forgot password modal ──
  const [showForgotModal, setShowForgotModal] = useState(false);

  // step: "email" | "otp" | "reset"
  const [step, setStep] = useState("email");
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState("");
  const [cssLoaded, setCssLoaded] = useState(false);

  useEffect(() => {
    const existing = document.querySelector("[data-auth='auth-css']");
    if (existing) {
      setCssLoaded(true);
      return;
    }

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "/admin/assets/css/styles.css";
    link.setAttribute("data-auth", "auth-css");

    link.onload = () => setCssLoaded(true);
    link.onerror = () => setCssLoaded(true);

    document.head.appendChild(link);

    return () => {
      document
        .querySelectorAll("[data-auth='auth-css']")
        .forEach((el) => el.remove());
    };
  }, []);

  if (!cssLoaded) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(135deg,#0d3b6e 0%,#185FA5 60%,#378ADD 100%)",
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            border: "3px solid rgba(255,255,255,0.3)",
            borderTop: "3px solid #fff",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validate = () => {
    const e = {};
    if (!form.emailOrMobile) e.emailOrMobile = "Email or mobile is required.";
    if (!form.password) e.password = "Password is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const doLogin = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await apiClient.post("/Auth/login", {
        emailOrMobile: form.emailOrMobile,
        password: form.password,
        role: ROLE_ID[activeRole],
      });
      if (res.statusCode !== 1) {
        toast.error(res.message || "Login failed.");
        return;
      }
      login(res.data);
      toast.success(res.message);
      navigate(ROLE_HOME[res.data.role] || "/login", { replace: true });
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Invalid credentials. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Modal open/close ──
  const openForgotModal = () => {
    setStep("email");
    setForgotEmail("");
    setOtp("");
    setNewPassword("");
    setConfirmPassword("");
    setResetToken("");
    setForgotError("");
    setShowForgotModal(true);
  };

  const closeForgotModal = () => {
    setShowForgotModal(false);
    setStep("email");
    setForgotEmail("");
    setOtp("");
    setNewPassword("");
    setConfirmPassword("");
    setResetToken("");
    setForgotError("");
  };

  // ── Step 1: Send OTP ──
  const handleSendOtp = async () => {
    if (!forgotEmail) {
      setForgotError("Please enter your email or mobile.");
      return;
    }
    setForgotLoading(true);
    setForgotError("");
    try {
      const res = await forgotPassword(forgotEmail);
      if (res.statusCode === 1) {
        toast.success(res.message || "OTP sent successfully.");
        setStep("otp");
      } else {
        setForgotError(res.message || "Failed to send OTP.");
      }
    } catch (err) {
      setForgotError(err.response?.data?.message || "Something went wrong.");
    } finally {
      setForgotLoading(false);
    }
  };

  // ── Step 2: Verify OTP ──
  const handleVerifyOtp = async () => {
    if (!otp) {
      setForgotError("Please enter the OTP.");
      return;
    }
    setForgotLoading(true);
    setForgotError("");
    try {
      const res = await verifyOtp(forgotEmail, otp);
      if (res.statusCode === 1) {
        toast.success(res.message);

        setResetToken(res.data.resetToken);
        setStep("reset");
      } else {
        setForgotError(res.message || "Invalid OTP.");
      }
    } catch (err) {
      setForgotError(
        err.response?.data?.message || "Invalid OTP. Please try again.",
      );
    } finally {
      setForgotLoading(false);
    }
  };

  // ── Step 3: Reset Password ──
  const handleResetPassword = async () => {
    if (!newPassword) {
      setForgotError("New password is required.");
      return;
    }
    if (newPassword.length < 8) {
      setForgotError("Minimum 8 characters required.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setForgotError("Passwords do not match.");
      return;
    }
    setForgotLoading(true);
    setForgotError("");
    try {
      const res = await resetPassword(resetToken, newPassword, confirmPassword);
      if (res.statusCode === 1) {
        toast.success(res.message);
        closeForgotModal();
      } else {
        setForgotError(res.message || "Failed to reset password.");
      }
    } catch (err) {
      setForgotError(err.response?.data?.message || "Something went wrong.");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div id="login-screen">
      <div className="login-card">
        <div className="login-logo">
           <img src="/admin/assets/image/logo.png" alt="Logo" height={40} />
          <div className="login-logo-sub">Property Management Platform</div>
        </div>

        <div className="login-tabs">
          {["saas", "owner", "tenant"].map((role) => (
            <button
              key={role}
              className={`login-tab ${activeRole === role ? "active" : ""}`}
              onClick={() => {
                setActiveRole(role);
                setErrors({});
              }}
            >
              {role === "saas"
                ? "SaaS Admin"
                : role === "owner"
                  ? "Property Owner"
                  : "Tenant"}
            </button>
          ))}
        </div>

        <div className="login-field">
          <label>Email / Mobile</label>
          <input
            type="text"
            name="emailOrMobile"
            value={form.emailOrMobile}
            onChange={handleChange}
            placeholder="Enter email or mobile"
            disabled={loading}
            style={errors.emailOrMobile ? { borderColor: "#dc2626" } : {}}
          />
          {errors.emailOrMobile && (
            <span
              style={{
                color: "#dc2626",
                fontSize: 11,
                marginTop: 3,
                display: "block",
              }}
            >
              {errors.emailOrMobile}
            </span>
          )}
        </div>

        <div className="login-field">
          <label>Password</label>
          <div style={{ position: "relative" }}>
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={form.password}
              onChange={handleChange}
              placeholder="Enter password"
              disabled={loading}
              style={{
                width: "100%",
                paddingRight: 38,
                ...(errors.password ? { borderColor: "#dc2626" } : {}),
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword((p) => !p)}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: 16,
                color: "#888",
                padding: 0,
              }}
            >
              {showPassword ? "🙈" : "👁"}
            </button>
          </div>
          {errors.password && (
            <span
              style={{
                color: "#dc2626",
                fontSize: 11,
                marginTop: 3,
                display: "block",
              }}
            >
              {errors.password}
            </span>
          )}
          <div style={{ textAlign: "right", marginTop: 6 }}>
            <button
              type="button"
              onClick={openForgotModal}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "var(--blue)",
                fontSize: 12,
                padding: 0,
              }}
            >
              Forgot password?
            </button>
          </div>
        </div>

        <button className="login-btn" onClick={doLogin} disabled={loading}>
          {loading ? "Signing in..." : "Sign In"}
        </button>
      </div>

      {/* ── Forgot Password Modal ── */}
      {showForgotModal && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 420 }}>
            <div className="modal-header">
              <div className="modal-title">
                {step === "email" && "Forgot Password"}
                {step === "otp" && "Verify OTP"}
                {step === "reset" && "Reset Password"}
              </div>
              <button className="modal-close" onClick={closeForgotModal}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              {/* Step indicator */}
              <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
                {["email", "otp", "reset"].map((s, i) => (
                  <div
                    key={s}
                    style={{
                      flex: 1,
                      height: 3,
                      borderRadius: 99,
                      background:
                        ["email", "otp", "reset"].indexOf(step) >= i
                          ? "var(--blue)"
                          : "#e5e7eb",
                    }}
                  />
                ))}
              </div>

              {/* Error */}
              {forgotError && (
                <div
                  style={{
                    background: "#fef2f2",
                    border: "1px solid #fca5a5",
                    borderRadius: 8,
                    padding: "10px 14px",
                    fontSize: 12,
                    color: "#991b1b",
                    marginBottom: 14,
                  }}
                >
                  {forgotError}
                </div>
              )}

              {/* ── Step 1: Email ── */}
              {step === "email" && (
                <>
                  <p
                    style={{
                      fontSize: 13,
                      color: "var(--text-secondary)",
                      marginBottom: 14,
                      marginTop: 0,
                    }}
                  >
                    Enter your registered email or mobile. We'll send you an
                    OTP.
                  </p>
                  <div className="form-group">
                    <label>Email / Mobile</label>
                    <input
                      type="text"
                      value={forgotEmail}
                      onChange={(e) => {
                        setForgotEmail(e.target.value);
                        setForgotError("");
                      }}
                      placeholder="Enter registered email or mobile"
                      disabled={forgotLoading}
                    />
                  </div>
                </>
              )}

              {/* ── Step 2: OTP ── */}
              {step === "otp" && (
                <>
                  <p
                    style={{
                      fontSize: 13,
                      color: "var(--text-secondary)",
                      marginBottom: 14,
                      marginTop: 0,
                    }}
                  >
                    OTP sent to <strong>{forgotEmail}</strong>. Enter it below.
                  </p>
                  <div className="form-group">
                    <label>OTP</label>
                    <input
                      type="text"
                      value={otp}
                      maxLength={6}
                      onChange={(e) => {
                        setOtp(e.target.value);
                        setForgotError("");
                      }}
                      placeholder="Enter 6-digit OTP"
                      disabled={forgotLoading}
                      style={{
                        letterSpacing: 6,
                        fontSize: 18,
                        textAlign: "center",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--text-tertiary)",
                      marginTop: 8,
                    }}
                  >
                    Didn't receive?{" "}
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      style={{
                        background: "none",
                        border: "none",
                        color: "var(--blue)",
                        cursor: "pointer",
                        fontSize: 12,
                        padding: 0,
                      }}
                    >
                      Resend OTP
                    </button>
                  </div>
                </>
              )}

              {/* ── Step 3: Reset Password ── */}
              {step === "reset" && (
                <>
                  <p
                    style={{
                      fontSize: 13,
                      color: "var(--text-secondary)",
                      marginBottom: 14,
                      marginTop: 0,
                    }}
                  >
                    Enter your new password below.
                  </p>
                  <div className="form-group">
                    <label>New Password</label>
                    <div style={{ position: "relative" }}>
                      <input
                        type={showNew ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          setForgotError("");
                        }}
                        placeholder="Min 8 characters"
                        disabled={forgotLoading}
                        style={{ width: "100%", paddingRight: 38 }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNew((p) => !p)}
                        style={{
                          position: "absolute",
                          right: 10,
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: 15,
                          color: "#888",
                        }}
                      >
                        {showNew ? "🙈" : "👁"}
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Confirm Password</label>
                    <div style={{ position: "relative" }}>
                      <input
                        type={showConfirm ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          setForgotError("");
                        }}
                        placeholder="Re-enter new password"
                        disabled={forgotLoading}
                        style={{ width: "100%", paddingRight: 38 }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirm((p) => !p)}
                        style={{
                          position: "absolute",
                          right: 10,
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: 15,
                          color: "#888",
                        }}
                      >
                        {showConfirm ? "🙈" : "👁"}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn"
                onClick={closeForgotModal}
                disabled={forgotLoading}
              >
                Cancel
              </button>
              {step === "email" && (
                <button
                  className="btn btn-primary"
                  onClick={handleSendOtp}
                  disabled={forgotLoading}
                >
                  {forgotLoading ? "Sending..." : "Send OTP"}
                </button>
              )}
              {step === "otp" && (
                <button
                  className="btn btn-primary"
                  onClick={handleVerifyOtp}
                  disabled={forgotLoading}
                >
                  {forgotLoading ? "Verifying..." : "Verify OTP"}
                </button>
              )}
              {step === "reset" && (
                <button
                  className="btn btn-primary"
                  onClick={handleResetPassword}
                  disabled={forgotLoading}
                >
                  {forgotLoading ? "Resetting..." : "Reset Password"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
